import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, RotateCcw, Save } from 'lucide-react';
import { adminApi } from '../services/api.jsx';
import { annoncerErreur, annoncerSucces, EtatErreur, SqueletteTableau } from '../etats';
import { useDroits } from '../context/Droits';

/* ═══════════════════════════════════════════════════════════════════════════
   RÔLES ET PERMISSIONS

   Ce tableau n'est pas une description de la règle : c'est la règle. Il est
   lu — et écrit — dans la même table que celle qui construit le menu et que
   le serveur oppose à chaque requête. Il ne peut donc pas devenir faux.

   Quatre droits par case, et non un niveau
   ----------------------------------------
   « Lecture / écriture » confondait des choses différentes. Pouvoir ajouter
   un coach n'implique pas de pouvoir en supprimer un. Et surtout, EXPORTER
   n'est pas consulter : on peut vouloir qu'un analyste lise les diagnostics
   sans qu'il sorte la base entière en tableur.

   « Voir » commande les trois autres : sans lui, la page n'existe pas pour
   ce rôle — absente du menu, et l'adresse répond « page introuvable », pas
   « accès refusé ». La nuance compte : refuser confirme que la page existe.
   Les trois autres cases sont donc désactivées tant que « voir » est décoché.
   ═══════════════════════════════════════════════════════════════════════════ */

const ACTIONS = [
  { cle: 'voir', lettre: 'V', libelle: 'Voir',
    aide: "Ouvre la page. Décoché, elle disparaît du menu et l'adresse répond « introuvable »." },
  { cle: 'ajouter', lettre: 'A', libelle: 'Ajouter',
    aide: 'Créer un élément dans cette zone.' },
  { cle: 'modifier', lettre: 'M', libelle: 'Modifier',
    aide: 'Modifier ou supprimer un élément existant.' },
  { cle: 'exporter', lettre: 'E', libelle: 'Exporter',
    aide: 'Télécharger un rapport ou un export de cette zone.' },
];

/* Une case à cocher réduite à une lettre : quatre par cellule, seize par
   ligne. Un libellé complet ferait un tableau de trois écrans de large ;
   la lettre porte l'info, l'infobulle et la légende portent le mot. */
function Case({ action, coche, desactive, onChange, zone, role }) {
  return (
    <label
      title={`${action.libelle} — ${zone} · ${role}`}
      className={`w-7 h-7 rounded-pastille grid place-items-center text-[11px] font-bold
                  transition-colors select-none
                  ${desactive
                    ? 'text-[var(--text-muted)] opacity-35 cursor-not-allowed'
                    : coche
                      ? 'bg-[var(--accent)] text-[#0B1620] cursor-pointer'
                      : 'bg-[var(--bg-main)] text-[var(--text-muted)] cursor-pointer hover:bg-[var(--border)]'}`}
    >
      <input
        type="checkbox"
        className="sr-only"
        checked={coche}
        disabled={desactive}
        onChange={(e) => onChange(action.cle, e.target.checked)}
      />
      <span aria-hidden="true">{action.lettre}</span>
      <span className="sr-only">{action.libelle} — {zone}, {role}</span>
    </label>
  );
}

export default function MatriceDesDroits() {
  const [matrice, setMatrice] = useState(null);
  // L'objet erreur du chargement : son statut choisit l'écran.
  const [erreur, setErreur] = useState(null);
  const [brouillon, setBrouillon] = useState(null);   // { role: { zone: {...} } }
  const [envoi, setEnvoi] = useState(false);
  /* Le menu du compte connecté lit la même table : s'il vient de se retirer
     une zone, elle doit disparaître de sa barre latérale tout de suite, et
     non à la prochaine connexion. */
  const { recharger } = useDroits();

  const charger = useCallback(() => {
    setErreur(null);
    adminApi.matriceDesDroits()
      .then((d) => { setMatrice(d); setBrouillon(null); })
      .catch((e) => setErreur(e));
  }, []);

  useEffect(() => { charger(); }, [charger]);

  /* L'état affiché : le brouillon s'il existe, la réponse du serveur sinon.
     Repartir de la réponse à chaque rendu effacerait les cases cochées. */
  const etat = useMemo(() => {
    if (!matrice) return {};
    if (brouillon) return brouillon;
    const base = {};
    (matrice.sections || []).forEach((s) => s.zones.forEach((z) => {
      Object.entries(z.acces || {}).forEach(([role, droits]) => {
        base[role] = base[role] || {};
        base[role][z.cle] = { ...droits };
      });
    }));
    return base;
  }, [matrice, brouillon]);

  const modifie = brouillon !== null;

  const basculer = (role, zone, action, valeur) => {
    setBrouillon((prec) => {
      const copie = JSON.parse(JSON.stringify(prec || etat));
      copie[role] = copie[role] || {};
      const cellule = { ...(copie[role][zone] || {}), [action]: valeur };
      // Décocher « voir » retire les autres : un rôle ne modifie pas une
      // page qu'il ne peut pas ouvrir, et l'écran le montre plutôt que de
      // laisser le serveur le corriger en silence.
      if (action === 'voir' && !valeur) {
        cellule.ajouter = false; cellule.modifier = false; cellule.exporter = false;
      }
      copie[role][zone] = cellule;
      return copie;
    });
  };

  const enregistrer = async () => {
    setEnvoi(true);
    try {
      const d = await adminApi.enregistrerMatriceDesDroits(etat);
      setMatrice(d);
      setBrouillon(null);
      recharger();
      annoncerSucces('Droits enregistrés. Ils s’appliquent immédiatement.');
    } catch (e) {
      annoncerErreur(e?.message || "Les droits n'ont pas pu être enregistrés.");
    } finally { setEnvoi(false); }
  };

  if (erreur && !matrice) return <EtatErreur erreur={erreur} onReessayer={charger} />;
  if (!matrice) return <SqueletteTableau colonnes={6} lignes={8} />;

  const roles = matrice.roles || [];
  const modifiable = Boolean(matrice.modifiable);

  return (
    <div className="space-y-4">
      <p className="text-[13px] text-[var(--text-muted)] max-w-3xl leading-relaxed">
        Ce tableau n’est pas une description : c’est la règle elle-même. Une
        entrée de menu absente ne protège rien — c’est cette table que le
        serveur oppose, que la page soit affichée ou que l’adresse soit saisie
        à la main.
        {modifiable
          ? ' Vos modifications prennent effet dès l’enregistrement.'
          : ' Seul un super administrateur peut la modifier.'}
      </p>

      {/* La légende dit les quatre mots une fois ; le tableau n’en garde
          que l’initiale. */}
      <div className="flex flex-wrap gap-x-5 gap-y-2">
        {ACTIONS.map((a) => (
          <span key={a.cle} className="inline-flex items-center gap-2 text-[12.5px] text-[var(--text-muted)]">
            <span className="w-6 h-6 rounded-pastille grid place-items-center text-[11px] font-bold
                             bg-[var(--accent)] text-[#0B1620]">{a.lettre}</span>
            <span><span className="font-semibold text-[var(--text-bold)]">{a.libelle}</span> — {a.aide}</span>
          </span>
        ))}
      </div>

      <div className="carte-tableau">
        <div className="tableau-hote">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr>
                <th scope="col">Zone</th>
                {roles.map((r) => (
                  <th key={r.cle} scope="col" className="text-center whitespace-nowrap">
                    {r.libelle}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(matrice.sections || []).map((section) => (
                <React.Fragment key={section.nom}>
                  <tr>
                    <th scope="colgroup" colSpan={roles.length + 1}
                        className="pt-5 pb-2 text-[var(--text-muted)]">
                      {section.nom}
                    </th>
                  </tr>
                  {section.zones.map((zone) => (
                    <tr key={zone.cle}>
                      <th scope="row" className="font-normal">
                        <span className="text-[13.5px] font-medium text-[var(--text-bold)]">
                          {zone.libelle}
                        </span>
                        <span className="block text-[11.5px] text-[var(--text-muted)]">
                          {zone.chemin}
                        </span>
                      </th>
                      {roles.map((r) => {
                        const cellule = etat[r.cle]?.[zone.cle] || {};
                        return (
                          <td key={r.cle} className="text-center">
                            <span className="inline-flex gap-1">
                              {ACTIONS.map((a) => (
                                <Case
                                  key={a.cle}
                                  action={a}
                                  coche={Boolean(cellule[a.cle])}
                                  desactive={!modifiable
                                    || (a.cle !== 'voir' && !cellule.voir)}
                                  onChange={(cle, v) => basculer(r.cle, zone.cle, cle, v)}
                                  zone={zone.libelle}
                                  role={r.libelle}
                                />
                              ))}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modifiable && (
        /* La barre n'apparaît qu'en cas de changement : un bouton
           « Enregistrer » toujours présent invite à cliquer sans raison,
           et on finit par enregistrer ce qu'on n'a pas voulu. */
        <div className={`flex flex-wrap items-center gap-3 transition-opacity ${
          modifie ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
          <p className="flex-1 text-[13px] text-[var(--text-muted)]">
            Modifications non enregistrées.
          </p>
          <button type="button" onClick={() => setBrouillon(null)}
                  className="act act-contour">
            <RotateCcw className="w-4 h-4" strokeWidth={2} />
            Annuler
          </button>
          <button type="button" onClick={enregistrer} disabled={envoi} aria-busy={envoi || undefined} className="act">
            {envoi ? <Loader2 className="w-4 h-4 animate-spin" />
                   : <Save className="w-4 h-4" strokeWidth={2} />}
            Enregistrer les droits
          </button>
        </div>
      )}
    </div>
  );
}
